import * as React from "react"
import { Toaster as Sonner } from "sonner"

type ToasterProps = React.ComponentProps<typeof Sonner>

/**
 * Tracks the theme the console is actually rendering.
 *
 * The app has no next-themes provider — `ThemeToggle` / `usePreferences` write
 * the `dark` class straight onto <html> from `localStorage.wicloud_theme`, so
 * `useTheme()` would always report "system" and let sonner drift away from the
 * console whenever the saved preference disagrees with the OS setting.
 */
function useDocumentTheme(): "light" | "dark" {
  const read = () =>
    document.documentElement.classList.contains("dark") ? "dark" : "light"
  const [theme, setTheme] = React.useState<"light" | "dark">(read)

  React.useEffect(() => {
    const root = document.documentElement
    const sync = () => setTheme(root.classList.contains("dark") ? "dark" : "light")
    sync()
    const observer = new MutationObserver(sync)
    observer.observe(root, { attributes: true, attributeFilter: ["class"] })
    return () => observer.disconnect()
  }, [])

  return theme
}

/**
 * Toasts follow the console surface tokens instead of sonner's own palette:
 * a `panel` background on a `hairline` border, with the status colour carried
 * only by the icon and a left accent bar. No close button — toasts auto-dismiss
 * and stay swipe/click dismissible.
 *
 * Sonner injects its stylesheet into <head> at runtime and OUTSIDE any cascade
 * layer, so it outranks every Tailwind utility (which live in @layer utilities)
 * regardless of selector specificity. Two escape hatches are therefore required
 * and must not be "cleaned up" into plain utility classes:
 *   - surface colours go through sonner's own `--normal-*` variables, set inline
 *     on the toaster root (inline styles ignore layers entirely);
 *   - anything sonner hardcodes (description colour, the border shorthand) needs
 *     the `!` important modifier, which reverses the layer ordering.
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const theme = useDocumentTheme()

  return (
    <Sonner
      theme={theme}
      className="toaster group"
      style={
        {
          "--normal-bg": "var(--panel)",
          "--normal-text": "var(--ink)",
          "--normal-border": "var(--hairline)",
        } as React.CSSProperties
      }
      toastOptions={{
        classNames: {
          toast: "group toast border-l-2!",
          title: "font-medium",
          description: "text-muted!",
          actionButton: "bg-brand! text-primary-foreground!",
          cancelButton: "bg-panel-2! text-muted!",
          success: "[border-left-color:var(--ok)]! [&_[data-icon]]:text-ok",
          error:
            "[border-left-color:var(--destructive)]! [&_[data-icon]]:text-destructive",
          warning: "[border-left-color:var(--warn)]! [&_[data-icon]]:text-warn",
          info: "[border-left-color:var(--info)]! [&_[data-icon]]:text-info",
          loading:
            "[border-left-color:var(--hairline-strong)]! [&_[data-icon]]:text-muted",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
